import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Megaphone,
  AlertCircle,
  Info,
  Loader2,
  Calendar
} from "lucide-react";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

export default function Announcements() {
  const { authAxios } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await authAxios.get('/announcements');
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <AlertCircle className="w-5 h-5 text-rose-500" />;
      case 'normal':
        return <Info className="w-5 h-5 text-emerald-500" />;
      default:
        return <Info className="w-5 h-5 text-stone-400" />;
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-rose-100 text-rose-800 border-rose-200',
      normal: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      low: 'bg-stone-100 text-stone-600 border-stone-200'
    };
    return (
      <Badge variant="outline" className={`${styles[priority] || styles.normal} text-xs uppercase tracking-wider`}>
        {priority}
      </Badge>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="announcements-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-stone-900 font-heading flex items-center gap-3">
          <Megaphone className="w-8 h-8 text-emerald-600" />
          Announcements
        </h1>
        <p className="text-stone-500 mt-1">Stay updated with company news</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : announcements.length === 0 ? (
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardContent className="p-12 text-center">
            <Megaphone className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-600">No announcements yet</h3>
            <p className="text-stone-400 mt-1">Check back later for updates</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <Card 
              key={announcement.id} 
              className={`border-0 shadow-lg shadow-stone-200/50 bg-white transition-all hover:shadow-xl ${
                announcement.priority === 'high' ? 'ring-2 ring-rose-200' : ''
              }`}
              data-testid={`announcement-${announcement.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    announcement.priority === 'high' ? 'bg-rose-100' : 'bg-emerald-100'
                  }`}>
                    {getPriorityIcon(announcement.priority)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-stone-900 font-heading">{announcement.title}</h3>
                      {getPriorityBadge(announcement.priority)}
                    </div>
                    <p className="text-stone-600 whitespace-pre-wrap leading-relaxed">{announcement.content}</p>
                    <div className="flex items-center gap-4 mt-4 text-sm text-stone-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {fmtTz(announcement.created_at, 'MMM d, yyyy')}
                      </span>
                      <span>by {announcement.created_by_name}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
